import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { SUPPORTED_LOCALES, LOCALE_NAMES, loadLocaleMessages } from '@/i18n'

const RTL_LOCALES = new Set(['ar', 'fa', 'he', 'ur'])

export function useLocale() {
  const { locale } = useI18n()
  const router = useRouter()
  const route = useRoute()

  const currentLocale = computed(() => locale.value)
  const isRTL = computed(() => RTL_LOCALES.has(locale.value))
  const dir = computed(() => isRTL.value ? 'rtl' : 'ltr')

  const availableLocales = computed(() =>
    SUPPORTED_LOCALES.map((code) => ({
      code,
      ...LOCALE_NAMES[code],
    }))
  )

  async function changeLocale(newLocale: string) {
    if (!SUPPORTED_LOCALES.includes(newLocale)) return
    await loadLocaleMessages(newLocale)
    locale.value = newLocale
    document.documentElement.lang = newLocale
    document.documentElement.dir = RTL_LOCALES.has(newLocale) ? 'rtl' : 'ltr'

    const currentRoute = route.fullPath
    const newPath = currentRoute.replace(`/${route.params.locale}`, `/${newLocale}`)
    await router.push(newPath)
  }

  function detectBrowserLocale(): string {
    const browserLang = navigator.language?.split('-')[0] ?? 'en'
    return SUPPORTED_LOCALES.includes(browserLang) ? browserLang : 'en'
  }

  return {
    currentLocale,
    isRTL,
    dir,
    availableLocales,
    changeLocale,
    detectBrowserLocale,
  }
}
